import { useCallback, useContext, useMemo, useRef, useState, useDeferredValue  } from "react";
import { CButton, CCol, CForm, CRow, CTable } from '@coreui/react';
import { Formik, FieldArray } from 'formik';
import { toast } from 'react-toastify';
import { ConnectedAccountContext } from "../context/ConnectedAccountContext";
import Waiting from "../../../../../../general/Loadings/Waiting";
import { CSmartPagination } from '@coreui/react-pro';
import StepCardHeader from "../components/StepCardHeaders";
import { CInputFile } from "../../../../../../migration/CInput";
import CFormGroup from "../../../../../../migration/CFormGroup";
import CLabel from "../../../../../../migration/CLabel";
import { ConfirmRemovePopup, ConfirmSaveChange } from "../../../../../../general/popup";
import FieldRow from '../components/FieldRow';
import { EX_DATATYPE } from "../../../../../../../constants/external-data";
import { callExternalDataApi } from "../../../../../../../apiCaller";
import { API_CUSTOM_EXTERNAL_DATA_CONNECTION } from "../../../../../../../constants";
import { normalizeExample } from "../../../../../../../utils";
import SyncFormikValues from "../../../../goals/intent-analysis/manage-intent-rules/components/SyncFormikValues";
import FormikDirtyWatcher from "../../../../goals/intent-analysis/manage-intent-rules/components/FormikDirtyWatcher";
import { cropCsvFile, dedupFiles, detectEmailType, getNewFields, guessFieldType, mergeHeadersStable, parseCsvFile, uniqueTopN, validateFiles } from "../../../../../../../helpers/external";

const CURRENT_STEP = 2;

const columns = [
    { key: 'fieldName', label: 'Field Name', _props: { scope: 'col' } },
    { key: 'dataType', label: 'Data Type', _props: { scope: 'col' } },
    { key: 'required', label: 'Required', _props: { scope: 'col' } },
    { key: 'example', label: 'Example', _props: { scope: 'col' } },
    { key: 'ignore', label: 'Ignore', _props: { scope: 'col' } },
];

const normalize = (s) => (s ?? '').trim().toLowerCase();

const SampleData = () => {
    const {
        activeStep,
        setActiveStep,
        stepsData,
        setStepsData,
        initStepsData,
        stepSubmitSnapshot,
        setStepSubmitSnapshot,
        onFormDirty,
        canUpdate
    } = useContext(ConnectedAccountContext);

    const fileInputRef = useRef();
    const formikRef = useRef(null);

    const [search, setSearch] = useState('');
    const deferredSearch = useDeferredValue(search);
    const [fieldsTemp, setFieldsTemp] = useState([]);
    const [newFieldsToAdd, setNewFieldsToAdd] = useState([]);
    const [process, setProcess] = useState(false);
    const [showAddFieldsPopup, setShowAddFieldsPopup] = useState(false);
    const [pendingHeaders, setPendingHeaders] = useState([]);
    const [pendingRows, setPendingRows] = useState([]);
    const [parsing, setParsing] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [showConfirmRemove, setShowConfirmRemove] = useState(false);
    const [downloadingIndex, setDownloadingIndex] = useState(null);

    const defaultInitialValuesStep = useMemo(() => ({
        files: initStepsData.fields?.files || [],
        headers: initStepsData.fields?.headers || [],
        fields: Array.isArray(initStepsData.fields?.fields) && initStepsData.fields?.fields?.length > 0
            ? initStepsData.fields.fields.map((field) => ({
                fieldName: field.value,
                dataType: field.type,
                required: field.required,
                ignore: field.ignore,
                example: field.example,
                type: field.customType,
            }))
            : [],
    }), [initStepsData]);

    const stepsDataStep = stepsData[CURRENT_STEP];
    const stepSnapshot = stepSubmitSnapshot[CURRENT_STEP];

    const initialValues = useMemo(
        () => stepsDataStep || stepSnapshot || defaultInitialValuesStep,
        [stepsDataStep, stepSnapshot, defaultInitialValuesStep]
    );

    const isMoreThanOneTrue = useMemo(() => {
        const map = stepsData?.importedByObjectUniqueIdObj ?? {}
        return Object.values(map).filter(Boolean).length >= 1
    }, [stepsData?.importedByObjectUniqueIdObj])

    // ===== submit =====
    const onSubmit = useCallback(async (values, { setSubmitting }) => {
        if (!values.fields.length) {
            toast.error("Fields must contain at least 1 item.");
            setSubmitting(false);
            return;
        }

        if (!values.files?.length) {
            toast.error("Please select at least one CSV file.");
            setSubmitting(false);
            return;
        }

        try {
            const CROPPED_LIMIT = 1000;
            const croppedFiles = await Promise.all(
                values.files.map(async (f) => {
                    try {
                        if (f.name.toLowerCase().endsWith('.csv')) {
                            return await cropCsvFile(f, CROPPED_LIMIT);
                        }
                        return f;
                    } catch {
                        return f;
                    }
                })
            );

            // Build payload fields
            const fieldsPayload = {
                fields: values.fields.map((field) => ({
                    value: field.fieldName,
                    label: field.fieldName,
                    type: field.dataType,
                    required: field.required,
                    ignore: field.ignore,
                    example: field.example,
                    customType: field.type,
                })),
            };

            const lastEditStep =
                stepsData.lastEditStep && stepsData.lastEditStep > CURRENT_STEP
                    ? stepsData.lastEditStep
                    : CURRENT_STEP;

            const formData = new FormData();
            croppedFiles.forEach((file) => {
                formData.append('fileData[]', file);
            });
            formData.append('fields', JSON.stringify(fieldsPayload));
            formData.append('lastEditStep', String(lastEditStep));
            formData.append('headers', JSON.stringify(values.headers ?? []));

            const response = await callExternalDataApi(
                `${API_CUSTOM_EXTERNAL_DATA_CONNECTION}/${stepsData.id}/files`,
                'PUT',
                formData
            );

            const isSuccess = response?.data?.code === 200;
            const responseData = response?.data?.data;

            if (isSuccess && responseData) {
                setStepsData({
                    ...stepsData,
                    fields: fieldsPayload,
                    lastEditStep,
                    ...(responseData || {}),
                    [CURRENT_STEP]: {
                        ...responseData.fields,
                        fields: responseData.fields.fields.map((field) => ({
                            fieldName: field.value,
                            dataType: field.type,
                            required: field.required,
                            ignore: field.ignore,
                            example: field.example,
                            type: field.customType,
                        }))
                    },
                    3: undefined,
                });

                toast.success('Sample data updated successfully.');
                setStepSubmitSnapshot(prev => ({
                    ...prev,
                    [CURRENT_STEP]: { ...values },
                }));
                setActiveStep(CURRENT_STEP + 1);
            } else {
                const errMsg =
                    response?.data?.message ||
                    'API Error: Something went wrong. Please try again later.';

                toast.error(errMsg);
            }
        } catch (err) {
            console.error('Submit error:', err);
            toast.error('Something went wrong. Please try again later.');
        } finally {
            setSubmitting(false);
            onFormDirty(CURRENT_STEP, false);
        }
    }, [setActiveStep, setStepsData, stepsData, onFormDirty, setStepSubmitSnapshot]);

    // ===== remove =====
    const handleRemoveFile = () => {
        setShowConfirmRemove(false);
        if (!formikRef.current) return;

        const files = formikRef.current.values?.files || [];
        const keep = files.filter(isDbFile);

        formikRef.current.setFieldValue("files", keep);
        formikRef.current.setFieldValue('fields', defaultInitialValuesStep.fields ?? []);
        formikRef.current.setFieldValue('headers', defaultInitialValuesStep.headers ?? []);

        if (fileInputRef.current) fileInputRef.current.value = '';
        setCurrentPage(1);
        setProcess(false);
    };

    // ===== process multi files =====
    const handleProcess = async () => {
        const files = formikRef.current?.values?.files.filter(
            (f) => f instanceof File
        ) || [];
        if (!files.length) {
            setProcess(false);
            toast.error("Please select at least one file.");
            return;
        }

        setParsing(true);

        try {
            const results = await Promise.all(files.map(f => parseCsvFile(f, 1001)));

            const perFileHeaders = files.map((f, i) => {
                const r = results[i];
                let headers = [];
                if (r && Array.isArray(r.data) && r.data.length > 0) {
                    const rows = r.data.filter((line) => Array.isArray(line) && line.length > 0);
                    if (rows.length > 0) {
                        headers = rows[0].map((h) => (h ?? '').toString().trim());
                    }
                }
                return { fileName: f.name || 'Unnamed', headers };
            });

            const headersList = [];
            const perFileRows = [];
            for (const r of results) {
                if (!Array.isArray(r.data) || r.data.length === 0) continue;
                const rows = r.data.filter((line) => Array.isArray(line) && line.length > 0);
                if (!rows.length) continue;

                const fileHeaders = rows[0].map((h) => (h ?? '').toString().trim());
                const fileDataRows = rows.slice(1);

                if (fileHeaders.length) headersList.push(fileHeaders);
                if (fileDataRows.length) perFileRows.push({ headers: fileHeaders, rows: fileDataRows });
            }
            
            const canonicalMap = new Map();

            const prevHeadersArr = formikRef.current?.values?.headers || [];
            for (const entry of prevHeadersArr) {
                if (!entry || !Array.isArray(entry.headers)) continue;
                for (const h of entry.headers) {
                    const raw = (h ?? '').toString().trim();
                    if (!raw) continue;
                    const key = normalize(raw);
                    if (!canonicalMap.has(key)) {
                        canonicalMap.set(key, raw);
                    }
                }
            }

            const prevFieldsArr = formikRef.current?.values?.fields || [];
            for (const f of prevFieldsArr) {
                if (!f || !f.fieldName) continue;

                const raw = (f.fieldName ?? '').toString().trim();
                if (!raw) continue;

                const key = normalize(raw);
                if (!canonicalMap.has(key)) {
                    canonicalMap.set(key, raw);
                }
            }

            const fixedPerFileHeaders = perFileHeaders.map((entry) => ({
                ...entry,
                headers: (entry.headers || []).map((h) => {
                    const raw = (h ?? '').toString().trim();
                    if (!raw) return raw;
                    const key = normalize(raw);
                    const canonical = canonicalMap.get(key);
                    return canonical || raw;
                }),
            }));

            const mergedByFile = new Map();

            for (const entry of prevHeadersArr) {
                if (!entry || typeof entry !== 'object') continue;

                const fileName = (entry.fileName ?? '').toString().trim();
                if (!fileName) continue;

                const key = fileName.toLowerCase();
                mergedByFile.set(key, {
                    fileName,
                    headers: Array.isArray(entry.headers) ? [...entry.headers] : [],
                });
            }

            for (const entry of fixedPerFileHeaders) {
                const fileName = (entry.fileName ?? '').toString().trim();
                if (!fileName) continue;

                const key = fileName.toLowerCase();
                const existing = mergedByFile.get(key);

                if (existing) {
                    mergedByFile.set(key, {
                        ...existing,
                        headers: Array.isArray(entry.headers) ? [...entry.headers] : [],
                    });
                } else {
                    mergedByFile.set(key, {
                        fileName,
                        headers: Array.isArray(entry.headers) ? [...entry.headers] : [],
                    });
                }
            }

            const finalHeadersArray = Array.from(mergedByFile.values());
            formikRef.current?.setFieldValue('headers', finalHeadersArray);

            if (!headersList.length) {
                toast.error('No valid data found.');
                return;
            }

            const headers = mergeHeadersStable(headersList);

            const normalizedRows = [];

            for (const { headers: fh, rows } of perFileRows) {
                const mapLower = new Map();
                fh.forEach((h, i) => mapLower.set(String(h).trim().toLowerCase(), i));

                for (const row of rows) {
                    const normalized = headers.map((h) => {
                        const idx = mapLower.get(h.toLowerCase());
                        return (idx !== undefined && idx !== null) ? row[idx] : undefined;
                    });
                    normalizedRows.push(normalized);
                }
            }

            const currentFieldNames = fieldsTemp.map((f) => f.fieldName);
            const fieldsToAdd = getNewFields(currentFieldNames, headers);

            if (
                fieldsToAdd.length > 0 &&
                Array.isArray(stepsData.fields?.fields) &&
                stepsData.fields?.fields?.length > 0
            ) {
                setPendingHeaders(headers);
                setPendingRows(normalizedRows);
                setNewFieldsToAdd(fieldsToAdd);
                setShowAddFieldsPopup(true);
            } else {
                const currentFields =
                    (fieldsTemp?.length > 0)
                        ? fieldsTemp
                        : (formikRef.current?.values?.fields ?? []);

                const map = new Map(
                    currentFields
                        .filter((f) => f && f.fieldName)
                        .map((f) => [normalize(f.fieldName), f])
                );

                headers.forEach((fieldName, idx) => {
                    const keyLc = normalize(fieldName);

                    const sampleVals = normalizedRows
                        .map(row => row[idx])
                        .filter(x => x !== undefined && x !== null && x !== "")
                        .slice(0, 10);

                    const detectedType = guessFieldType(sampleVals);
                    const emailOrDomainType = detectEmailType(sampleVals);

                    const exampleArr = [];
                    for (let v of sampleVals) {
                        if (detectedType === EX_DATATYPE.TIMESTAMP) {
                            const normalizedEx = normalizeExample(v);
                            if (normalizedEx && !exampleArr.includes(normalizedEx)) {
                                exampleArr.push(normalizedEx);
                            }
                        } else if (!exampleArr.includes(v)) {
                                exampleArr.push(v);
                            }
                        if (exampleArr.length >= 3) break;
                    }

                    const existing = map.get(keyLc);

                    const merged = existing
                        ? {
                            ...existing,
                            fieldName: existing.fieldName,
                            example: uniqueTopN(
                                [ ...(existing.example ?? []), ...exampleArr ],
                                3
                            ),
                            ...(emailOrDomainType ? { type: emailOrDomainType } : {}),
                            dataType: existing.dataType || (
                                fieldName.toLowerCase().includes("phone")
                                    ? EX_DATATYPE.STRING
                                    : detectedType
                            ),
                        }
                        : {
                            fieldName,
                            dataType: fieldName.toLowerCase().includes("phone")
                                ? EX_DATATYPE.STRING
                                : detectedType,
                            required: 'no',
                            ignore: false,
                            example: exampleArr,
                            ...(emailOrDomainType ? { type: emailOrDomainType } : {}),
                        };

                    if (merged.fieldName.toLowerCase().includes('phone')) {
                        merged.dataType = EX_DATATYPE.STRING;
                    }

                    map.set(keyLc, merged);
                });

                const finalFields = Array.from(map.values());

                setFieldsTemp([]);
                formikRef.current.setFieldValue('fields', finalFields);
            }
        } catch (e) {
            console.error(e);
            toast.error('File parsing error.');
        } finally {
            setParsing(false);
            setProcess(false);
        }
    };

    // ===== handle file input change =====
    const handleFileChange = (e, setFieldValue) => {
        const picked = Array.from(e.target.files || []);
        const prev = (formikRef.current?.values?.files) ?? [];

        const merged = dedupFiles([...prev, ...picked]);

        const errorMsg = validateFiles(picked);
        if (errorMsg) {
            toast.error(errorMsg);
            e.target.value = '';
            setProcess(false);
            return;
        }

        setFieldValue('files', merged);
        setFieldsTemp(formikRef.current.values.fields ?? []);
        setProcess(true);

        e.target.value = '';
    };

    /* ------------- Add/merge new fields (RESTORED & UPGRADED) ------------- */
    const handleAddNewFields = () => {
        const currentFields = formikRef.current?.values.fields ?? [];

        const addedCandidates = (newFieldsToAdd || []).map((f) => ({
            fieldName: f,
            dataType: EX_DATATYPE.STRING,
            required: 'no',
            ignore: false,
            example: [],
            isNew: true
        }));

        const headerCandidates = (pendingHeaders || []).map((fieldName, idx) => {
            const sampleVals = (pendingRows || [])
                .map((row) => row[idx])
                .filter((x) => x !== undefined && x !== null && x !== "")
                .slice(0, 10);

            const type = guessFieldType(sampleVals);
            const emailOrDomainType = detectEmailType(sampleVals);

            const tmp = [];
            for (const v of sampleVals) {
                if (type === EX_DATATYPE.TIMESTAMP) {
                    const normalized = normalizeExample(v);
                    if (normalized && !tmp.includes(normalized)) tmp.push(normalized);
                }
                if (!tmp.includes(v)) tmp.push(v);
                if (tmp.length >= 3) break;
            }

            return {
                fieldName,
                dataType: type,
                required: 'no',
                ignore: false,
                example: uniqueTopN(tmp, 3),
                ...(emailOrDomainType ? { type: emailOrDomainType } : {})
            };
        });

        const candidates = [...addedCandidates, ...headerCandidates];

        const map = new Map(currentFields.map((f) => [f.fieldName, f]));

        for (const c of candidates) {
            const prev = map.get(c.fieldName);
            const merged = {
                fieldName: c.fieldName,
                dataType: c.dataType ?? prev?.dataType ?? EX_DATATYPE.STRING,
                required: prev?.required ?? c.required ?? 'no',
                ignore: prev?.ignore ?? c.ignore ?? false,
                example: uniqueTopN([...(prev?.example ?? []), ...(c.example ?? [])], 3),
                isNew: prev?.isNew ?? c.isNew ?? false,
                ...(c.type ? { type: c.type } : prev?.type ? { type: prev.type } : {})
            };

            if (c.fieldName.toLowerCase().includes("phone")) {
                merged.dataType = EX_DATATYPE.STRING;
            }

            // Optional: protect from unintended downgrade (TIMESTAMP -> STRING)
            if (prev?.dataType === EX_DATATYPE.TIMESTAMP && c.dataType === EX_DATATYPE.STRING) {
                merged.dataType = prev.dataType;
            }

            map.set(c.fieldName, merged);
        }

        const currentOrder = currentFields.map((f) => f.fieldName);
        const newOrder = candidates
            .map((c) => c.fieldName)
            .filter((name) => !currentOrder.includes(name));
        const finalOrder = [...currentOrder, ...newOrder];

        const dedupByFieldNameLast = (arr) =>
            Array.from(
                new Map(arr.map((item) => [normalize(item.fieldName), item])).values()
            );

        const dedupFields = finalOrder.map((name) => map.get(name));
        const deduped = dedupByFieldNameLast(dedupFields);

        const originalNameByLc = new Map(
            currentFields
                .filter((f) => f && f.fieldName)
                .map((f) => {
                    const raw = (f.fieldName ?? '').toString().trim();
                    return [normalize(raw), f];
                })
        );

        const restored = deduped.map((f) => {
            const raw = (f.fieldName ?? '').toString().trim();
            const key = normalize(raw);
            const original = originalNameByLc.get(key);
            return original
                ? { ...original }
                : f;
        });

        formikRef.current?.setFieldValue('fields', restored);

        setShowAddFieldsPopup(false);
        setNewFieldsToAdd([]);
        setPendingHeaders([]);
        setPendingRows([]);
        setProcess(false);
    };

    const isDbFile = (f) => !(f instanceof File);
    const fileDisplayName = (f) => f?.name || f?.fileName || 'Unnamed';

    const handleDownload = async ({
        filePath,
        fileName
    }, index) => {
        try {
            setDownloadingIndex(index)

            const q = new URLSearchParams({
                filePath,
                ...(fileName ? { filename: fileName } : {}),
            })

            const res = await callExternalDataApi(
                `${API_CUSTOM_EXTERNAL_DATA_CONNECTION}/${stepsData.id}/signed-url?${q.toString()}`,
            );

            if (res.status !== 200) {
                const err = res.data;
                toast.error(err.message || 'Failed to get signed URL');
            }
            const { url } = res.data?.data;
            downloadFile(url, fileName);
        } catch (e) {
            toast.error('Download error');
        } finally {
            setDownloadingIndex(null)
        }
    };

    const handleRemoveOneFile = useCallback((indexToRemove) => {
        if (!formikRef.current) return;

        const values = formikRef.current.values;
        const files = values?.files ?? [];
        const headersArr = values?.headers ?? [];
        const fields = values?.fields ?? [];

        const targetFile = files[indexToRemove];
        if (!(targetFile instanceof File)) return;

        const nextFiles = files.filter((_, idx) => idx !== indexToRemove);
        formikRef.current.setFieldValue("files", nextFiles);

        const removedHeaderEntry = headersArr[indexToRemove];
        const nextHeaders = headersArr.filter((_, idx) => idx !== indexToRemove);
        formikRef.current.setFieldValue("headers", nextHeaders);

        if (removedHeaderEntry && Array.isArray(removedHeaderEntry.headers)) {
            const removedHeaders = removedHeaderEntry.headers.map(h => (h ?? "").toString().trim().toLowerCase());

            const allRemainingHeaders = nextHeaders
                .flatMap(h => h.headers)
                .map(h => (h ?? "").toString().trim().toLowerCase());

            const filteredFields = fields.filter(f => {
                const fname = (f.fieldName ?? "").toString().trim().toLowerCase();
                return !removedHeaders.includes(fname) || allRemainingHeaders.includes(fname);
            });

            formikRef.current.setFieldValue("fields", filteredFields);
        }

        const stillHasNew = nextFiles.some(f => f instanceof File);
        setProcess(stillHasNew);

        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }

        setCurrentPage(prev => {
            const total = Math.max(0, nextFiles.length);
            const pageSize = 10;
            const totalPages = Math.max(1, Math.ceil(total / pageSize));
            return Math.min(prev, totalPages);
        });
    }, []);

    async function downloadFile(url, fileName) {
        const res = await fetch(url);
        if (!res.ok) toast.error('Failed to fetch file');

        const blob = await res.blob();
        const link = document.createElement('a');

        link.href = window.URL.createObjectURL(blob);
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(link.href);
    }

    return (
        <StepCardHeader
            title={`Sample Data`}
            step={CURRENT_STEP}
            activeStep={activeStep}
            lastEditStep={stepsData.lastEditStep}
            setActiveStep={() => setActiveStep(CURRENT_STEP)}
        >
            <p>Upload a csv data file that contains examples of the data you will send. Each row should represent one record or event and each column should represent a field you want to import. After uploading your sample, you'll have the opportunity to identify the objects inside of the data. You will then separately manage the settings for all of the objects.</p>

            <ConfirmRemovePopup
                show={showConfirmRemove}
                onAccept={handleRemoveFile}
                onClose={() => setShowConfirmRemove(false)}
                title='Delete this file?'
            >
                <p>You are about to delete this file. Please confirm.</p>
            </ConfirmRemovePopup>

            {showAddFieldsPopup && (
                <ConfirmSaveChange
                    show={showAddFieldsPopup}
                    onAccept={handleAddNewFields}
                    onClose={() => {
                        setShowAddFieldsPopup(false);
                        setNewFieldsToAdd([]);
                        setPendingHeaders([]);
                        setPendingRows([]);
                    }}
                    title='Add new fields?'
                    acceptText="Add fields"
                    rejectText="Cancel"
                    className="confirm-add-fields-popup"
                >
                    <p>New fields detected: <b>{newFieldsToAdd.join(', ')}</b></p>
                    <p>Do you want to add these fields to the mapping configuration?</p>
                </ConfirmSaveChange>
            )}

            <Formik
                innerRef={formikRef}
                initialValues={initialValues}
                onSubmit={onSubmit}
                validateOnBlur={true}
                enableReinitialize
            >
                {({ values, setFieldValue, isSubmitting, handleSubmit, touched }) => {
                    const filteredFiles = values.files.filter(
                        (f) => f instanceof File
                    );
                    const fileError = touched.files && validateFiles(filteredFiles);
                    const fields = values.fields || [];
                    const pageSize = 10;

                    const filteredFields = () => {
                        const q = (deferredSearch || '').trim().toLowerCase();
                        if (!q) return fields;
                        return fields.filter(f => (f.fieldName || '').toLowerCase().includes(q));
                    };

                    const totalFields = filteredFields().length;
                    const totalPages = Math.ceil(totalFields / pageSize);
                    const startIdx = (currentPage - 1) * pageSize;
                    const fieldsWithIndex = (values.fields || []).map((f, i) => ({ field: f, originalIndex: i }));
                    const filteredWithIndex = (deferredSearch || '').trim()
                        ? fieldsWithIndex.filter(({ field }) =>
                            (field.fieldName || '').toLowerCase().includes(deferredSearch.toLowerCase())
                            )
                        : fieldsWithIndex;
                    const paginatedWithIndex = filteredWithIndex.slice(startIdx, startIdx + pageSize);

                    return (
                        <CForm onSubmit={handleSubmit} noValidate>
                            <SyncFormikValues
                                values={values}
                                setStepsData={setStepsData}
                                step={CURRENT_STEP}
                            />
                            <FormikDirtyWatcher
                                step={CURRENT_STEP}
                                values={values}
                                snapshot={stepSubmitSnapshot[CURRENT_STEP]}
                                defaultInitialValues={defaultInitialValuesStep}
                                onDirty={onFormDirty}
                            />
                            <CRow>
                                {(values.files?.length > 0) && (
                                    <CCol lg="12" className="mb-2">
                                        <div className="d-flex flex-wrap gap-2 align-items-center">
                                            {values.files.map((f, i) => {
                                                const removable = !isDbFile(f);
                                                const name = fileDisplayName(f);

                                                return (
                                                    <span
                                                        key={`${name}-${i}`}
                                                        className="badge bg-light text-dark d-inline-flex align-items-center"
                                                        style={{ gap: 6 }}
                                                    >
                                                        {name}
                                                        {removable && (
                                                            <button
                                                                type="button"
                                                                onClick={() => handleRemoveOneFile(i)}
                                                                className="btn btn-link btn-sm p-0 ms-1"
                                                                title="Remove this file"
                                                                aria-label={`Remove ${name}`}
                                                            >
                                                            ✖️
                                                            </button>
                                                        )}
                                                        {!removable && (
                                                            <>
                                                                <span className="ms-1 text-muted" title="Loaded from database – cannot remove">🔒</span>

                                                                <button
                                                                    type="button"
                                                                    onClick={() =>
                                                                        handleDownload({
                                                                            filePath: f.filePath,
                                                                            fileName: f.fileName || name,
                                                                        }, i)
                                                                    }
                                                                    className="btn btn-link btn-sm p-0 ms-1"
                                                                    title="Download file"
                                                                >
                                                                    {downloadingIndex === i ? (
                                                                            <span
                                                                                className="spinner-border spinner-border-sm text-secondary"
                                                                                role="status"
                                                                                aria-hidden="true"
                                                                            />
                                                                        ) : (
                                                                            "⬇️"
                                                                        )}
                                                                </button>
                                                            </>
                                                        )}
                                                    </span>
                                                );
                                            })}

                                            {values.files.some((f) => !isDbFile(f)) && (
                                                <CButton type="button" color="danger" size="sm" onClick={() => setShowConfirmRemove(true)}>
                                                    Remove files
                                                </CButton>
                                            )}
                                        </div>
                                    </CCol>
                                )}

                                <CCol lg="6">
                                    <CFormGroup className="mb-0 position-relative form-group-ex-files">
                                        <CInputFile
                                            innerRef={fileInputRef}
                                            accept=".csv"
                                            custom
                                            multiple
                                            id="custom-file-input"
                                            onChange={(e) => handleFileChange(e, setFieldValue)}
                                            disabled={isSubmitting}
                                        />
                                        <CLabel htmlFor="custom-file-input text-truncate" variant="custom-file">
                                            {values.files?.length
                                                ? `${values.files.length} Files`
                                                : 'Choose CSV files...'}
                                        </CLabel>
                                        {!isSubmitting && fileError && <div className="invalid-feedback d-block">{fileError}</div>}
                                    </CFormGroup>
                                </CCol>

                                <CCol lg="6">
                                    <CButton
                                        type="button"
                                        color="primary"
                                        onClick={handleProcess}
                                        disabled={!(values.files && values.files.length) || parsing || !process || canUpdate}
                                    >
                                        <Waiting isLoading={parsing}>Upload &amp; Process</Waiting>
                                    </CButton>
                                </CCol>

                                <CCol lg="12" className="mt-4">
                                    <p>We found the following fields in your file. When you provide data in the future (via manual upload or API calls) we will expect these fields in the payload based on the settings you include below. Adjust the settings as needed.</p>

                                    <div className="d-flex align-items-start gap-2">
                                        <div className="position-relative mb-4" style={{ minWidth: 280 }}>
                                            <input
                                                type="text"
                                                aria-label="Search field name"
                                                placeholder="Search field name…"
                                                className="form-control"
                                                value={search}
                                                onChange={(e) => {
                                                    setSearch(e.target.value);
                                                    setCurrentPage(1);
                                                }}
                                            />

                                            {search && (
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-link position-absolute"
                                                    style={{ right: 6, top: 6 }}
                                                    onClick={() => {
                                                        setSearch('');
                                                        setCurrentPage(1);
                                                    }}
                                                >
                                                    Clear
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    <FieldArray name="fields">
                                        {() => (
                                            <>
                                                <CTable hover striped>
                                                    <thead>
                                                        <tr>
                                                            {columns.map(col => (
                                                                <th
                                                                    key={col.key}
                                                                    style={col.key === 'fieldName' || col.key === 'example' ? { width: '300px', maxWidth: '300px' } : {}}
                                                                >{col.label}</th>
                                                            ))}
                                                        </tr>
                                                    </thead>

                                                    <tbody>
                                                        {paginatedWithIndex.map(({ field, originalIndex }) => (
                                                            <FieldRow
                                                                key={`${field.fieldName}-${originalIndex}`}
                                                                field={field}
                                                                idx={originalIndex}
                                                                stepsData={stepsData}
                                                                setFieldValue={setFieldValue}
                                                                isMoreThanOneTrue={isMoreThanOneTrue}
                                                            />
                                                        ))}
                                                    </tbody>
                                                </CTable>

                                                {totalFields > pageSize && (
                                                    <div className="d-flex justify-content-center mt-3">
                                                        <CSmartPagination
                                                            activePage={currentPage}
                                                            pages={totalPages}
                                                            onActivePageChange={setCurrentPage}
                                                        />
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </FieldArray>
                                </CCol>
                            </CRow>

                            <div className="mt-3">
                                <CButton
                                    disabled={isSubmitting || canUpdate || (!(values.files && values.files.length) && !stepsData.fields?.fileName) || !values.fields || !!fileError || !(parsing || !process || canUpdate)}
                                    type="submit"
                                    className="px-4"
                                    color="primary"
                                >
                                    <Waiting isLoading={isSubmitting}>Save & Next</Waiting>
                                </CButton>
                            </div>
                        </CForm>
                    );
                }}
            </Formik>
        </StepCardHeader>
    );
};
export default SampleData;
